import { InfiniteData, Query, QueryClient } from '@tanstack/react-query'

import {
  Event,
  EventRegistrationStatus,
  PaginatedEvent,
} from '@tribeplatform/gql-client/types'

import { getEventKey, getEventsListKey } from './event.key.js'

interface UpdateCachedEventProps {
  newEvent: Event
  queryClient: QueryClient
}

export const updateCachedEvent = ({
  newEvent,
  queryClient,
}: UpdateCachedEventProps) => {
  queryClient.setQueryData(
    getEventKey({ variables: { id: newEvent.id } }),
    newEvent,
  )

  const queryKeys = getQueriesContainingEvent(queryClient, newEvent.id)
  queryClient.invalidateQueries(queryKeys)
}

interface UpdateEventRegistrationStatusProps {
  eventId: string
  queryClient: QueryClient
  status: EventRegistrationStatus
}

export const updateEventRegistrationStatus = ({
  eventId,
  queryClient,
  status,
}: UpdateEventRegistrationStatusProps) => {
  const updateRegistrationStatus = (event: Event) => ({
    ...event,
    authMemberProps: {
      ...event?.authMemberProps,
      eventRegistration: {
        ...event?.authMemberProps?.eventRegistration,
        status,
      },
    },
  })

  queryClient.setQueryData<Event>(
    getEventKey({ variables: { id: eventId } }),
    old => updateRegistrationStatus(old),
  )

  const queryKeys = getQueriesContainingEvent(queryClient, eventId)
  queryClient.invalidateQueries(queryKeys)
}

export const getAllEventQueries = (queryClient: QueryClient) => {
  const eventsKey = getEventsListKey()
  return queryClient
    .getQueryCache()
    .getAll()
    .filter(query => query?.queryKey?.at(0) === eventsKey[0]) as Query<
    InfiniteData<PaginatedEvent>
  >[]
}

export const getQueriesContainingEvent = (
  queryClient: QueryClient,
  eventId: string,
) => {
  const allEventQueries = getAllEventQueries(queryClient)
  const hasEvent = allEventQueries.filter(query =>
    query.state.data?.pages
      .flatMap(page => page.nodes)
      .some(node => node.id === eventId),
  )
  return hasEvent
}
